import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  QrCode,
  Download,
  Trash2,
  Loader2,
  Store,
  X,
  Hash,
  ScanLine
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

import { tablesAPI, establishmentAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkCreateStart, setBulkCreateStart] = useState('');
  const [bulkCreateEnd, setBulkCreateEnd] = useState('');
  const [bulkDeleteStart, setBulkDeleteStart] = useState('');
  const [bulkDeleteEnd, setBulkDeleteEnd] = useState('');

  const [selectedQR, setSelectedQR] = useState(null);
  const [selectedQRTitle, setSelectedQRTitle] = useState('');

  const [establishmentQR, setEstablishmentQR] = useState(null);
  const [loadingEstablishmentQR, setLoadingEstablishmentQR] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('links');
  const [exportLoading, setExportLoading] = useState(false);
  const [layout, setLayout] = useState({ columns: 2, qrSize: 150, showTableNumber: true, showLink: true });

  const loadTables = async () => {
    try {
      setLoading(true);
      const response = await tablesAPI.getAll();
      setTables(response.data || []);
    } catch (err) {
      toast.error('Impossible de charger les tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, []);

  const handleCreateTable = async (e) => {
    e.preventDefault();

    const parsed = Number(newTableNumber);

    if (!newTableNumber || Number.isNaN(parsed) || parsed <= 0) {
      toast.error('Veuillez entrer un numéro de table valide');
      return;
    }

    try {
      setCreating(true);
      await tablesAPI.create({ table_number: parsed });
      toast.success(`Table ${parsed} créée`);
      setNewTableNumber('');
      await loadTables();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de créer la table');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTable = async (tableId, tableNumber) => {
    const confirmed = window.confirm(`Supprimer la table ${tableNumber} ?`);
    if (!confirmed) return;

    try {
      await tablesAPI.delete(tableId);
      toast.success(`Table ${tableNumber} supprimée`);
      await loadTables();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de supprimer la table');
    }
  };

  const parseRange = (startValue, endValue) => {
    const start = Number(startValue);
    const end = Number(endValue);

    if (!startValue || !endValue || Number.isNaN(start) || Number.isNaN(end) || start <= 0 || end <= 0 || start > end) {
      return null;
    }

    return { start_number: start, end_number: end, count: end - start + 1 };
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();

    const range = parseRange(bulkCreateStart, bulkCreateEnd);
    if (!range) {
      toast.error('Veuillez entrer une plage de tables valide');
      return;
    }

    try {
      setBulkCreating(true);
      const response = await tablesAPI.createBulk({
        start_number: range.start_number,
        end_number: range.end_number
      });
      const createdCount = response.data.created_count || 0;
      const skippedCount = response.data.skipped_existing?.length || 0;

      if (createdCount > 0 && skippedCount > 0) {
        toast.success(`${createdCount} table(s) creee(s), ${skippedCount} deja existante(s)`);
      } else if (createdCount > 0) {
        toast.success(`${createdCount} table(s) creee(s)`);
      } else {
        toast.info('Toutes les tables de cette plage existent deja');
      }

      setBulkCreateStart('');
      setBulkCreateEnd('');
      await loadTables();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de creer les tables');
    } finally {
      setBulkCreating(false);
    }
  };

  const handleBulkDelete = async (e) => {
    e.preventDefault();

    const range = parseRange(bulkDeleteStart, bulkDeleteEnd);
    if (!range) {
      toast.error('Veuillez entrer une plage de tables valide');
      return;
    }

    const confirmed = window.confirm(
      `Supprimer les tables ${range.start_number} a ${range.end_number} ?`
    );
    if (!confirmed) return;

    try {
      setBulkDeleting(true);
      const response = await tablesAPI.deleteBulk({
        start_number: range.start_number,
        end_number: range.end_number
      });
      toast.success(`${response.data.deleted_count || 0} table(s) supprimee(s)`);
      setBulkDeleteStart('');
      setBulkDeleteEnd('');
      await loadTables();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de supprimer les tables');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openTableQR = async (table) => {
    try {
      const response = await tablesAPI.getQR(table.id);
      setSelectedQR(response.data.qr_code);
      setSelectedQRTitle(`QR code – Table ${table.table_number}`);
    } catch (err) {
      toast.error('Impossible de charger le QR code de la table');
    }
  };

  const openEstablishmentQR = async () => {
    try {
      setLoadingEstablishmentQR(true);
      const response = await establishmentAPI.getEntryQR();
      setEstablishmentQR(response.data.qr_code);
      setSelectedQR(response.data.qr_code);
      setSelectedQRTitle('QR code établissement');
    } catch (err) {
      toast.error("Impossible de charger le QR code de l'établissement");
    } finally {
      setLoadingEstablishmentQR(false);
    }
  };

  const downloadBase64Image = (base64, filename) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportTablesPdf = async () => {
    if (!tables.length) {
      toast.error('Aucune table à exporter');
      return;
    }
    setExportLoading(true);
    let printWindow;
    try {
      printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Autorisez les fenêtres contextuelles pour générer le PDF');
        return;
      }
      const items = await Promise.all(tables.map(async (table) => {
        const response = await tablesAPI.getQR(table.id);
        return { number: table.table_number, qr: response.data.qr_code, url: response.data.url };
      }));
      const cells = items.map(({ number, qr, url }) => {
        const qrMarkup = exportFormat === 'links' ? '' : `<img class="qr" src="data:image/png;base64,${qr}" style="width:${layout.qrSize}px;height:${layout.qrSize}px" alt="QR table ${number}">`;
        const numberMarkup = layout.showTableNumber ? `<h2>Table ${number}</h2>` : '';
        const linkMarkup = layout.showLink || exportFormat === 'links' ? `<a href="${url}">${url}</a>` : '';
        if (exportFormat === 'links') return `<article class="link-row">${numberMarkup}<a href="${url}">${url}</a></article>`;
        if (exportFormat === 'qrs') return `<article class="qr-card">${numberMarkup}${qrMarkup}</article>`;
        return `<article class="combined">${numberMarkup}<div class="combined-body">${qrMarkup}<a href="${url}">${url}</a></div></article>`;
      }).join('');
      const columns = exportFormat === 'links' ? 1 : layout.columns;
      const title = { links: 'Liens des tables', qrs: 'QR codes des tables', combined: 'Tables · liens et QR codes' }[exportFormat];
      const doc = printWindow.document;
      doc.open();
      doc.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${title}</title><style>
        @page{size:A4;margin:15mm}*{box-sizing:border-box}body{font:14px Arial,sans-serif;color:#111827;margin:0}
        h1{font-size:22px;margin:0 0 7px}.subtitle{color:#64748b;font-size:11px;margin:0 0 20px}
        .grid{display:grid;grid-template-columns:repeat(${columns},minmax(0,1fr));gap:14px}
        article{break-inside:avoid;border:1px solid #dbe2ea;border-radius:12px;padding:12px;min-width:0}
        h2{font-size:15px;margin:0 0 8px}.qr{display:block;object-fit:contain;margin:0 auto}
        .qr-card{text-align:center}.combined-body{display:flex;align-items:center;gap:12px}
        .combined-body .qr{flex:0 0 auto;margin:0}.combined-body a{overflow-wrap:anywhere}
        a{color:#1d4ed8;overflow-wrap:anywhere}.link-row{display:flex;gap:12px;align-items:center}
        .link-row h2{min-width:85px;margin:0}
        @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
        </style></head><body><h1>${title}</h1><p class="subtitle">${tables.length} tables · GlassOrder</p><main class="grid">${cells}</main>
        <script>window.addEventListener('load',()=>setTimeout(()=>window.print(),300));window.addEventListener('afterprint',()=>window.close());<\/script>
        </body></html>`);
      doc.close();
      setExportOpen(false);
    } catch (err) {
      printWindow?.close();
      toast.error('Impossible de préparer le PDF. Réessayez.');
    } finally {
      setExportLoading(false);
    }
  };

  const closeQRModal = () => {
    setSelectedQR(null);
    setSelectedQRTitle('');
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Tables & QR codes
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Gérez vos tables et choisissez entre QR direct par table ou QR générique établissement.
        </p>
      </div>

      <section className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-violet-700">
              <Store className="w-4 h-4" />
              QR établissement
            </div>

            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              QR générique du restaurant
            </h2>

            <p className="mt-2 text-sm leading-7 text-gray-500">
              Ce QR redirige vers l’établissement puis demande au client de saisir son numéro de
              table. Pratique si vous voulez utiliser un QR unique pour plusieurs tables, zones
              ou événements.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={openEstablishmentQR}
              disabled={loadingEstablishmentQR}
            >
              {loadingEstablishmentQR ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <QrCode className="w-4 h-4 mr-2" />
              )}
              Voir le QR
            </Button>

            {establishmentQR && (
              <Button
                type="button"
                className="rounded-full bg-[#111827] hover:bg-black"
                onClick={() => downloadBase64Image(establishmentQR, 'glassorder-establishment-qr.png')}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
              <ScanLine className="w-4 h-4" />
              QR par table
            </div>

            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Ajouter une table
            </h2>

            <p className="mt-2 text-sm text-gray-500">
              Le QR de table redirige directement le client vers sa table, sans étape
              supplémentaire.
            </p>
          </div>

          <form onSubmit={handleCreateTable} className="flex w-full max-w-md items-center gap-3">
            <div className="relative flex-1">
              <Hash className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                type="number"
                min="1"
                placeholder="Numéro de table"
                value={newTableNumber}
                onChange={(e) => setNewTableNumber(e.target.value)}
                className="h-12 rounded-full pl-11"
              />
            </div>

            <Button
              type="submit"
              disabled={creating}
              className="h-12 rounded-full bg-[#111827] px-5 hover:bg-black"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      <section className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
            <Hash className="w-4 h-4" />
            Gestion en lot
          </div>

          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Créer ou supprimer plusieurs tables
          </h2>

          <p className="mt-2 text-sm leading-7 text-gray-500">
            Entrez une plage de numéros. Les tables déjà existantes sont conservées à la création.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <form onSubmit={handleBulkCreate} className="rounded-3xl border border-gray-200 bg-gray-50 p-5">
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Créer une plage
            </h3>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Input
                type="number"
                min="1"
                placeholder="De"
                value={bulkCreateStart}
                onChange={(e) => setBulkCreateStart(e.target.value)}
                className="h-12 rounded-full"
              />
              <Input
                type="number"
                min="1"
                placeholder="A"
                value={bulkCreateEnd}
                onChange={(e) => setBulkCreateEnd(e.target.value)}
                className="h-12 rounded-full"
              />
            </div>

            <Button
              type="submit"
              disabled={bulkCreating}
              className="mt-4 h-12 w-full rounded-full bg-[#111827] hover:bg-black"
            >
              {bulkCreating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer la plage
                </>
              )}
            </Button>
          </form>

          <form onSubmit={handleBulkDelete} className="rounded-3xl border border-red-100 bg-red-50 p-5">
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Supprimer une plage
            </h3>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Input
                type="number"
                min="1"
                placeholder="De"
                value={bulkDeleteStart}
                onChange={(e) => setBulkDeleteStart(e.target.value)}
                className="h-12 rounded-full bg-white"
              />
              <Input
                type="number"
                min="1"
                placeholder="A"
                value={bulkDeleteEnd}
                onChange={(e) => setBulkDeleteEnd(e.target.value)}
                className="h-12 rounded-full bg-white"
              />
            </div>

            <Button
              type="submit"
              disabled={bulkDeleting}
              className="mt-4 h-12 w-full rounded-full bg-red-600 hover:bg-red-700"
            >
              {bulkDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer la plage
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-[28px] border border-violet-100 bg-violet-50/60 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Exporter les accès des tables</h2>
          <p className="mt-1 text-sm text-gray-600">Créez un document PDF prêt à imprimer avec liens, QR codes ou les deux.</p>
        </div>
        <Button type="button" onClick={() => setExportOpen(true)} disabled={loading || tables.length === 0} className="rounded-full">
          <Download className="h-4 w-4" /> Exporter en PDF
        </Button>
      </section>

      <section className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Liste des tables
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Chaque table dispose de son propre QR code direct.
          </p>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Chargement des tables...
          </div>
        ) : tables.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <p className="text-sm font-medium text-gray-700">Aucune table pour le moment</p>
            <p className="mt-2 text-sm text-gray-500">
              Créez votre première table pour générer un QR code direct.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {tables
              .slice()
              .sort((a, b) => a.table_number - b.table_number)
              .map((table) => (
                <motion.div
                  key={table.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl border border-gray-200 bg-gray-50 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        Table
                      </p>
                      <h3
                        className="mt-1 text-2xl font-bold text-gray-900"
                        style={{ fontFamily: 'Manrope' }}
                      >
                        {table.table_number}
                      </h3>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openTableQR(table)}
                        className="rounded-full p-2 bg-white border border-gray-200 hover:bg-gray-100"
                        title="Voir le QR"
                      >
                        <QrCode className="w-4 h-4 text-gray-700" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteTable(table.id, table.table_number)}
                        className="rounded-full p-2 bg-white border border-gray-200 hover:bg-red-50"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => openTableQR(table)}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Voir le QR
                    </Button>

                    <Button
                      type="button"
                      className="flex-1 rounded-full bg-[#111827] hover:bg-black"
                      onClick={async () => {
                        try {
                          const response = await tablesAPI.getQR(table.id);
                          downloadBase64Image(
                            response.data.qr_code,
                            `glassorder-table-${table.table_number}.png`
                          );
                        } catch (err) {
                          toast.error('Impossible de télécharger le QR code');
                        }
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </motion.div>
              ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selectedQR && (
          <motion.div
            className="mobile-modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeQRModal}
          >
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="mobile-modal-panel w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-violet-600">QR code</p>
                  <h3
                    className="mt-1 text-xl font-bold text-gray-900"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    {selectedQRTitle}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeQRModal}
                  className="rounded-full p-2 hover:bg-gray-100"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 flex items-center justify-center">
                <img
                  src={`data:image/png;base64,${selectedQR}`}
                  alt={selectedQRTitle}
                  className="w-72 h-72 object-contain"
                />
              </div>

              <div className="mt-5 flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={closeQRModal}
                >
                  Fermer
                </Button>

                <Button
                  type="button"
                  className="flex-1 rounded-full bg-[#111827] hover:bg-black"
                  onClick={() => {
                    const safeName = selectedQRTitle
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^\w-]/g, '');

                    downloadBase64Image(selectedQR, `${safeName}.png`);
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Exporter les tables en PDF</DialogTitle>
            <DialogDescription>Choisissez le contenu et sa disposition. Le navigateur ouvrira ensuite l’impression : sélectionnez « Enregistrer au format PDF ».</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-semibold">Contenu à exporter</legend>
              {[
                ['links', 'Liste des liens uniquement'],
                ['qrs', 'Tous les QR codes à la suite'],
                ['combined', 'Pour chaque table : QR code et lien']
              ].map(([value, label]) => <label key={value} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm">
                <input type="radio" name="table-pdf-format" value={value} checked={exportFormat === value} onChange={() => setExportFormat(value)} />
                {label}
              </label>)}
            </fieldset>
            {exportFormat !== 'links' && <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">QR par rangée
                <select value={layout.columns} onChange={e => setLayout({...layout,columns:Number(e.target.value)})} className="mt-1 h-11 w-full rounded-xl border bg-white px-3">
                  <option value={1}>1 colonne</option><option value={2}>2 colonnes</option><option value={3}>3 colonnes</option>
                </select>
              </label>
              <label className="text-sm">Taille du QR
                <select value={layout.qrSize} onChange={e => setLayout({...layout,qrSize:Number(e.target.value)})} className="mt-1 h-11 w-full rounded-xl border bg-white px-3">
                  <option value={110}>Petit</option><option value={150}>Moyen</option><option value={190}>Grand</option>
                </select>
              </label>
            </div>}
            <div className="space-y-2">
              <label className="flex min-h-10 items-center gap-3 text-sm"><input type="checkbox" checked={layout.showTableNumber} onChange={e=>setLayout({...layout,showTableNumber:e.target.checked})}/> Afficher le numéro de table</label>
              {exportFormat === 'combined' && <label className="flex min-h-10 items-center gap-3 text-sm"><input type="checkbox" checked={layout.showLink} onChange={e=>setLayout({...layout,showLink:e.target.checked})}/> Afficher le lien sous le QR code</label>}
            </div>
            <p className="text-xs text-gray-500">Les liens restent cliquables dans le PDF. Dans l’écran d’impression, vous pouvez également ajuster papier, orientation et marges.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={()=>setExportOpen(false)}>Annuler</Button>
            <Button type="button" disabled={exportLoading} onClick={exportTablesPdf}>{exportLoading && <Loader2 className="h-4 w-4 animate-spin"/>}Générer le PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
